import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'

function Timestamp() {
  const { t } = useTranslation()
  const [unixTimestamp, setUnixTimestamp] = useState('')
  const [dateString, setDateString] = useState('')
  const [currentTimestamp, setCurrentTimestamp] = useState(Math.floor(Date.now() / 1000))
  const [timezone, setTimezone] = useState('local')

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimestamp(Math.floor(Date.now() / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const formatDate = (timestamp: number, tz: string): string => {
    const date = new Date(timestamp * 1000)
    if (tz === 'utc') {
      return date.toISOString().replace('T', ' ').replace('.000Z', ' UTC')
    }
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  }

  const handleUnixChange = (value: string) => {
    setUnixTimestamp(value)
    const num = parseInt(value)
    if (!isNaN(num) && value.length > 0) {
      // Handle milliseconds (13 digits) vs seconds (10 digits)
      const ts = value.length >= 13 ? Math.floor(num / 1000) : num
      setDateString(formatDate(ts, timezone))
    } else {
      setDateString('')
    }
  }

  const handleDateChange = (value: string) => {
    setDateString(value)
    const date = new Date(value)
    if (!isNaN(date.getTime())) {
      setUnixTimestamp(Math.floor(date.getTime() / 1000).toString())
    }
  }

  const useCurrentTimestamp = () => {
    setUnixTimestamp(currentTimestamp.toString())
    setDateString(formatDate(currentTimestamp, timezone))
  }

  const handleTimezoneChange = (tz: string) => {
    setTimezone(tz)
    if (unixTimestamp) {
      const num = parseInt(unixTimestamp)
      if (!isNaN(num)) {
        const ts = unixTimestamp.length >= 13 ? Math.floor(num / 1000) : num
        setDateString(formatDate(ts, tz))
      }
    }
  }

  const getRelativeTime = (timestamp: number): string => {
    const now = Math.floor(Date.now() / 1000)
    const diff = now - timestamp
    
    if (diff < 0) {
      const absDiff = Math.abs(diff)
      if (absDiff < 60) return `${t('toolPages.jwtDecoder.in')} ${absDiff} ${t('toolPages.jwtDecoder.seconds')}`
      if (absDiff < 3600) return `${t('toolPages.jwtDecoder.in')} ${Math.floor(absDiff / 60)} ${t('toolPages.jwtDecoder.minutes')}`
      if (absDiff < 86400) return `${t('toolPages.jwtDecoder.in')} ${Math.floor(absDiff / 3600)} ${t('toolPages.jwtDecoder.hours')}`
      return `${t('toolPages.jwtDecoder.in')} ${Math.floor(absDiff / 86400)} ${t('toolPages.jwtDecoder.days')}`
    }
    
    if (diff < 60) return `${diff} ${t('toolPages.jwtDecoder.seconds')} ${t('toolPages.jwtDecoder.ago')}`
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t('toolPages.jwtDecoder.minutes')} ${t('toolPages.jwtDecoder.ago')}`
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t('toolPages.jwtDecoder.hours')} ${t('toolPages.jwtDecoder.ago')}`
    if (diff < 2592000) return `${Math.floor(diff / 86400)} ${t('toolPages.jwtDecoder.days')} ${t('toolPages.jwtDecoder.ago')}`
    if (diff < 31536000) return `${Math.floor(diff / 2592000)} months ${t('toolPages.jwtDecoder.ago')}`
    return `${Math.floor(diff / 31536000)} years ${t('toolPages.jwtDecoder.ago')}`
  }

  const parsedTimestamp = parseInt(unixTimestamp)
  const isValidTimestamp = !isNaN(parsedTimestamp) && unixTimestamp.length > 0

  return (
    <Layout showBackButton title={t('toolPages.timestamp.title')}>
      <div className="flex flex-col p-4 gap-6">
        {/* Current Time Display */}
        <div className="bg-white dark:bg-surface-dark rounded-xl p-4 border border-slate-200 dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {t('toolPages.timestamp.currentTimestamp')}
            </span>
            <button
              onClick={useCurrentTimestamp}
              className="text-xs font-medium text-primary hover:text-primary-700 transition-colors"
            >
              {t('toolPages.timestamp.useThis')}
            </button>
          </div>
          <div className="font-mono text-2xl font-bold text-primary">
            {currentTimestamp}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {formatDate(currentTimestamp, timezone)}
          </div>
        </div>

        {/* Timezone Toggle */}
        <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
          <button
            onClick={() => handleTimezoneChange('local')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              timezone === 'local'
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-surface-dark text-slate-700 dark:text-slate-300'
            }`}
          >
            {t('toolPages.timestamp.localTime')}
          </button>
          <button
            onClick={() => handleTimezoneChange('utc')}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              timezone === 'utc'
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-surface-dark text-slate-700 dark:text-slate-300'
            }`}
          >
            {t('toolPages.timestamp.utc')}
          </button>
        </div>

        {/* Unix Timestamp Input */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-end px-1">
            <label className="section-label">{t('toolPages.timestamp.unixTimestamp')}</label>
            <CopyButton text={unixTimestamp} />
          </div>
          <input
            type="text"
            value={unixTimestamp}
            onChange={(e) => handleUnixChange(e.target.value)}
            className="input font-mono text-lg"
            placeholder="e.g., 1704067200"
          />
        </div>

        {/* Date/Time Input */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-end px-1">
            <label className="section-label">{t('toolPages.timestamp.dateTime')}</label>
            <CopyButton text={dateString} />
          </div>
          <input
            type="datetime-local"
            value={dateString ? new Date(parseInt(unixTimestamp) * 1000).toISOString().slice(0, 16) : ''}
            onChange={(e) => handleDateChange(e.target.value)}
            className="input"
          />
          {dateString && (
            <div className="text-sm text-slate-500 dark:text-slate-400 px-1">
              {dateString}
            </div>
          )}
        </div>

        {/* Additional Formats */}
        {isValidTimestamp && (
          <div className="flex flex-col gap-3">
            <h3 className="section-label">{t('toolPages.timestamp.additionalFormats')}</h3>
            <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
              <div className="flex justify-between items-center p-3">
                <span className="text-sm text-slate-500 dark:text-slate-400">{t('toolPages.timestamp.milliseconds')}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{parsedTimestamp * 1000}</span>
                  <CopyButton text={(parsedTimestamp * 1000).toString()} />
                </div>
              </div>
              <div className="flex justify-between items-center p-3">
                <span className="text-sm text-slate-500 dark:text-slate-400">{t('toolPages.timestamp.iso8601')}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{new Date(parsedTimestamp * 1000).toISOString()}</span>
                  <CopyButton text={new Date(parsedTimestamp * 1000).toISOString()} />
                </div>
              </div>
              <div className="flex justify-between items-center p-3">
                <span className="text-sm text-slate-500 dark:text-slate-400">{t('toolPages.timestamp.relative')}</span>
                <span className="text-sm text-primary">{getRelativeTime(parsedTimestamp)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Bottom spacing */}
        <div className="h-8"></div>
      </div>
    </Layout>
  )
}

export default Timestamp
